import { useState, useEffect, useCallback } from "react";
import ExportWithPhotos from "@/components/admin/ExportWithPhotos";
import { api, downloadAuthed } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import ImageUploader from "@/components/ImageUploader";
import LabelEditor from "@/components/admin/LabelEditor";
import EeTurboImportModal from "@/components/admin/products/EeTurboImportModal";
import {
    RefreshCw,
    Package,
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    Play,
    Pause,
    Search,
    AlertCircle,
    FileText,
    Image as ImageIcon,
    Upload,
    Download,
    Printer,
} from "lucide-react";

// ---------- Products Tab ----------
const USAGE_COLUMNS = [
    { key: "turbo_no", label: "Turbo No." },
    { key: "turbo_model", label: "Turbo model" },
    { key: "car_make", label: "Car make" },
    { key: "car_model", label: "Car model" },
    { key: "engine_displacement", label: "Engine displ." },
    { key: "engine_power", label: "Power kW/HP" },
    { key: "year_of_manufacture", label: "Years" },
    { key: "oem", label: "OEM" },
];

export default function ProductsTab() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [filter, setFilter] = useState({ q: "", category_id: "", brand_id: "", aftermarket_brand_id: "" });
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [confirm, setConfirm] = useState(null);
    const [galleryInput, setGalleryInput] = useState("");
    const [bulkStatus, setBulkStatus] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [eeImport, setEeImport] = useState(null); // {code, loading, preview, error, result}
    // Multi-select for bulk QR labels
    const [selectedIds, setSelectedIds] = useState([]);
    const [qrLayout, setQrLayout] = useState("3x4");
    const [labelEditorOpen, setLabelEditorOpen] = useState(false);

    const perPage = 25;

    const load = useCallback(async () => {
        const params = { page, per_page: perPage };
        if (filter.q) params.q = filter.q;
        if (filter.category_id) params.category_id = filter.category_id;
        if (filter.brand_id) params.brand_id = filter.brand_id;
        if (filter.aftermarket_brand_id) params.aftermarket_brand_id = filter.aftermarket_brand_id;
        const countParams = { q: filter.q, category_id: filter.category_id, brand_id: filter.brand_id, aftermarket_brand_id: filter.aftermarket_brand_id };
        const [pRes, cRes] = await Promise.all([
            api.get("/products", { params }),
            api.get("/products/count", { params: countParams }),
        ]);
        setProducts(pRes.data);
        setTotal(cRes.data.count);
    }, [page, filter]);

    useEffect(() => {
        (async () => {
            const [catRes, brandRes] = await Promise.all([
                api.get("/categories"),
                api.get("/brands"),
            ]);
            setCategories(catRes.data);
            setBrands(brandRes.data);
        })();
    }, []);

    useEffect(() => { load(); }, [load]);

    // Bulk enrich polling
    const loadBulkStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/products/enrich-all/status");
            setBulkStatus(data);
            return data;
        } catch { return null; }
    }, []);

    useEffect(() => { loadBulkStatus(); }, [loadBulkStatus]);

    useEffect(() => {
        if (!bulkStatus?.running) return;
        const id = setInterval(async () => {
            const s = await loadBulkStatus();
            if (s && !s.running) {
                clearInterval(id);
                load();
            }
        }, 3000);
        return () => clearInterval(id);
    }, [bulkStatus?.running, loadBulkStatus, load]);

    const startBulkEnrich = async (missingOnly) => {
        try {
            await api.post("/admin/products/enrich-all", null, { params: { missing_only: missingOnly } });
            await loadBulkStatus();
        } catch (e) {
            alert(e.response?.data?.detail || "Failed to start");
        }
    };

    const pauseBulk = async () => {
        try { await api.post("/admin/products/enrich-all/pause"); await loadBulkStatus(); }
        catch (e) { alert(e.response?.data?.detail || e.message); }
    };
    const resumeBulk = async () => {
        try { await api.post("/admin/products/enrich-all/resume"); await loadBulkStatus(); }
        catch (e) { alert(e.response?.data?.detail || e.message); }
    };
    const cancelBulk = async () => {
        if (!confirm("Ndalo procesin e enrich-it?")) return;
        try { await api.post("/admin/products/enrich-all/cancel"); await loadBulkStatus(); }
        catch (e) { alert(e.response?.data?.detail || e.message); }
    };

    const blank = () => ({
        name: "",
        category_id: categories[0]?.id || "",
        brand_id: "",
        aftermarket_brand_id: "",
        image_url: "",
        gallery: [],
        description: "",
        note: "",
        part_number: "",
        price: "",
        stock: "on_order",
        stock_qty: 0,
        usage: [],
        specifications: [],
    });

    const openNew = () => { setEditing(blank()); setError(""); setGalleryInput(""); };

    const openEdit = async (p) => {
        try {
            const { data } = await api.get(`/products/${p.id}`);
            setEditing({
                id: data.id,
                name: data.name,
                category_id: data.category_id,
                brand_id: data.brand_id || "",
                aftermarket_brand_id: data.aftermarket_brand_id || "",
                image_url: data.image_url || "",
                gallery: Array.isArray(data.gallery) ? data.gallery : [],
                description: data.description || "",
                note: data.note || "",
                part_number: data.part_number || "",
                price: data.price ?? "",
                stock: data.stock,
                stock_qty: data.stock_qty ?? 0,
                usage: Array.isArray(data.usage) ? data.usage : [],
                specifications: Array.isArray(data.specifications) ? data.specifications : [],
            });
            setError("");
            setGalleryInput("");
        } catch (e) {
            setError("Could not load product");
        }
    };

    const save = async () => {
        setError("");
        const payload = {
            name: editing.name,
            category_id: editing.category_id,
            brand_id: editing.brand_id || null,
            aftermarket_brand_id: editing.aftermarket_brand_id || null,
            image_url: editing.image_url || null,
            gallery: editing.gallery || [],
            description: editing.description || null,
            note: editing.note || null,
            part_number: editing.part_number || null,
            price: editing.price === "" ? null : parseFloat(editing.price),
            stock: editing.stock,
            stock_qty: Number.isFinite(parseInt(editing.stock_qty, 10)) ? parseInt(editing.stock_qty, 10) : 0,
            usage: editing.usage || [],
            specifications: editing.specifications || [],
        };
        try {
            if (editing.id) {
                await api.put(`/products/${editing.id}`, payload);
            } else {
                await api.post("/products", payload);
            }
            setEditing(null);
            load();
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed");
        }
    };

    const askRemove = (p) => {
        setConfirm({
            title: "Delete product",
            message: `Delete "${p.name}"? This cannot be undone.`,
            onConfirm: async () => {
                await api.delete(`/products/${p.id}`);
                setConfirm(null);
                load();
            },
        });
    };

    const addGalleryUrl = () => {
        const url = galleryInput.trim();
        if (!url) return;
        setEditing({ ...editing, gallery: [...editing.gallery, url] });
        setGalleryInput("");
    };

    const removeGalleryUrl = (i) => {
        const next = editing.gallery.filter((_, idx) => idx !== i);
        setEditing({ ...editing, gallery: next });
    };

    const addUsageRow = () => {
        setEditing({
            ...editing,
            usage: [...editing.usage, Object.fromEntries(USAGE_COLUMNS.map((c) => [c.key, ""]))],
        });
    };

    const updateUsageCell = (idx, key, value) => {
        const next = editing.usage.map((row, i) => (i === idx ? { ...row, [key]: value } : row));
        setEditing({ ...editing, usage: next });
    };

    const removeUsageRow = (idx) => {
        setEditing({ ...editing, usage: editing.usage.filter((_, i) => i !== idx) });
    };

    const addSpecRow = () => {
        setEditing({
            ...editing,
            specifications: [...(editing.specifications || []), { label: "", value: "", unit: "" }],
        });
    };

    const updateSpecCell = (idx, key, value) => {
        const next = (editing.specifications || []).map((row, i) =>
            i === idx ? { ...row, [key]: value } : row
        );
        setEditing({ ...editing, specifications: next });
    };

    const removeSpecRow = (idx) => {
        setEditing({
            ...editing,
            specifications: (editing.specifications || []).filter((_, i) => i !== idx),
        });
    };

    const enrichFromSource = async () => {
        if (!editing?.id) return;
        try {
            const { data } = await api.post(`/products/${editing.id}/enrich`);
            // Reload the product into the form
            const fresh = await api.get(`/products/${editing.id}`);
            const p = fresh.data;
            setEditing({
                ...editing,
                gallery: Array.isArray(p.gallery) ? p.gallery : editing.gallery,
                note: p.note || editing.note,
                usage: Array.isArray(p.usage) ? p.usage : editing.usage,
            });
            setError(
                `Fetched from source: ${data.gallery_count} images, ${data.usage_count} usage rows` +
                (data.has_note ? ", note added" : "")
            );
        } catch (e) {
            setError(e.response?.data?.detail || "Enrich failed");
        }
    };

    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div data-testid="admin-products-tab">
            {/* Bulk enrich status/action bar */}
            <div className="border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                    <div className="label-kicker text-slate-500 mb-0.5">Bulk enrich from ee-turbo.eu</div>
                    {bulkStatus?.running ? (
                        <div className="flex items-center gap-3">
                            <div className="font-mono text-xs">
                                {bulkStatus.paused
                                    ? <span className="text-amber-600 font-bold">⏸ PAUSED</span>
                                    : <span className="text-emerald-600 font-bold">● RUNNING</span>}
                                {" "}
                                <span className="text-slate-700">
                                    {bulkStatus.done}/{bulkStatus.total}
                                </span>
                                {bulkStatus.failed > 0 && <span className="text-red-600"> · {bulkStatus.failed} failed</span>}
                                {bulkStatus.current && <span className="text-slate-400 ml-2">→ {bulkStatus.current}</span>}
                            </div>
                            <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                                <div
                                    className="h-full bg-[hsl(var(--primary))] transition-all"
                                    style={{ width: `${bulkStatus.total ? ((bulkStatus.done + bulkStatus.failed) / bulkStatus.total) * 100 : 0}%` }}
                                />
                            </div>
                        </div>
                    ) : bulkStatus?.finished_at ? (
                        <div className="font-mono text-xs text-slate-600">
                            ✓ Last run: <span className="text-slate-900">{bulkStatus.done} enriched</span>
                            {bulkStatus.failed > 0 && <span className="text-red-600">, {bulkStatus.failed} failed</span>}
                            {" "}· {new Date(bulkStatus.finished_at).toLocaleString()}
                        </div>
                    ) : (
                        <div className="font-mono text-xs text-slate-500">
                            Fetch gallery + note + usage table for all imported products in one go.
                        </div>
                    )}
                </div>
                {bulkStatus?.running ? (
                    <>
                        {bulkStatus.paused ? (
                            <button onClick={resumeBulk} className="btn-primary" data-testid="bulk-enrich-resume">
                                <Play className="w-3.5 h-3.5" /> Resume
                            </button>
                        ) : (
                            <button onClick={pauseBulk} className="btn-outline" data-testid="bulk-enrich-pause">
                                <Pause className="w-3.5 h-3.5" /> Pause
                            </button>
                        )}
                        <button onClick={cancelBulk} className="btn-outline" data-testid="bulk-enrich-cancel">
                            <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                    </>
                ) : (
                    <>
                        <button onClick={() => startBulkEnrich(true)} className="btn-outline" data-testid="bulk-enrich-missing">
                            <RefreshCw className="w-3.5 h-3.5" /> Enrich missing only
                        </button>
                        <button onClick={() => startBulkEnrich(false)} className="btn-primary" data-testid="bulk-enrich-all">
                            <Play className="w-3.5 h-3.5" /> Fetch ALL from source
                        </button>
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={filter.q}
                        onChange={(e) => { setFilter({ ...filter, q: e.target.value }); setPage(1); }}
                        placeholder="Search name or part number..."
                        className="input-field !pl-9"
                        data-testid="products-search"
                    />
                </div>
                <select
                    value={filter.category_id}
                    onChange={(e) => { setFilter({ ...filter, category_id: e.target.value }); setPage(1); }}
                    className="input-field !w-auto max-w-[220px]"
                    data-testid="products-category-filter"
                >
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select
                    value={filter.brand_id}
                    onChange={(e) => { setFilter({ ...filter, brand_id: e.target.value }); setPage(1); }}
                    className="input-field !w-auto max-w-[220px]"
                    data-testid="products-brand-filter"
                >
                    <option value="">All OEM brands</option>
                    {brands.filter((b) => b.kind === "oem").map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select
                    value={filter.aftermarket_brand_id}
                    onChange={(e) => { setFilter({ ...filter, aftermarket_brand_id: e.target.value }); setPage(1); }}
                    className="input-field !w-auto max-w-[220px]"
                    data-testid="products-aftermarket-filter"
                >
                    <option value="">All aftermarket</option>
                    {brands.filter((b) => b.kind === "aftermarket").map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <button onClick={openNew} className="btn-primary" data-testid="new-product-button">
                    <Plus className="w-4 h-4" /> New product
                </button>
                <button
                    onClick={() => setEeImport({ code: "", loading: false, preview: null, error: "", result: null })}
                    className="btn-outline"
                    data-testid="ee-import-button"
                    title="Import a single product from ee-turbo.eu by its code"
                >
                    <Download className="w-4 h-4" /> ee-turbo import
                </button>
                <ExportWithPhotos
                    kind="products"
                    plainPath="/admin/products/export"
                    plainName="turbo-cimi-catalog.xlsx"
                    params={{ brand_id: filter.brand_id || undefined, aftermarket_brand_id: filter.aftermarket_brand_id || undefined, category_id: filter.category_id || undefined }}
                    testid="export-xlsx-button"
                />
                <button
                    type="button"
                    onClick={() => setLabelEditorOpen(true)}
                    className="btn-primary"
                    data-testid="open-label-designer"
                    title="Open Label Designer — drag-and-drop editor for OCBP-M810 thermal labels"
                >
                    <FileText className="w-4 h-4" /> Label Designer
                </button>
                <label className="btn-outline cursor-pointer" data-testid="import-xlsx-label">
                    <Upload className="w-4 h-4" /> Import XLSX
                    <input
                        type="file"
                        accept=".xlsx,.xlsm"
                        className="hidden"
                        data-testid="import-xlsx-input"
                        onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const fd = new FormData();
                            fd.append("file", f);
                            try {
                                const { data } = await api.post("/admin/products/import", fd, {
                                    headers: { "Content-Type": "multipart/form-data" },
                                });
                                setImportResult(data);
                                load();
                            } catch (err) {
                                setImportResult({ error: err.response?.data?.detail || "Import failed" });
                            } finally {
                                e.target.value = "";
                            }
                        }}
                    />
                </label>
            </div>

            {importResult && (
                <div className="mb-3 border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="import-result-banner">
                    {importResult.error ? (
                        <span className="text-red-600 font-mono">Import error: {importResult.error}</span>
                    ) : (
                        <div className="flex items-center gap-4 flex-wrap font-mono text-xs">
                            <span className="text-emerald-700 font-bold">✓ Imported {importResult.updated}/{importResult.processed}</span>
                            {importResult.unmatched_count > 0 && (
                                <span className="text-amber-700">⚠ {importResult.unmatched_count} unmatched</span>
                            )}
                            {importResult.invalid_rows > 0 && (
                                <span className="text-red-600">✗ {importResult.invalid_rows} invalid</span>
                            )}
                            <button onClick={() => setImportResult(null)} className="text-slate-500 ml-auto">×</button>
                        </div>
                    )}
                </div>
            )}

            {selectedIds.length > 0 && (
                <div
                    className="mb-3 sticky top-2 z-10 border-2 border-slate-900 bg-white shadow-[4px_4px_0_0_hsl(var(--primary))] p-3 flex items-center gap-3 flex-wrap"
                    data-testid="bulk-qr-toolbar"
                >
                    <span className="font-display font-black uppercase text-sm">
                        {selectedIds.length} selected
                    </span>
                    <button
                        type="button"
                        onClick={() => setSelectedIds([])}
                        className="text-xs font-mono text-slate-500 underline"
                        data-testid="bulk-qr-clear"
                    >
                        Clear
                    </button>
                    <div className="ml-auto flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Layout:</span>
                        <select
                            value={qrLayout}
                            onChange={(e) => setQrLayout(e.target.value)}
                            className="input-field !py-1 !text-xs !w-auto"
                            data-testid="bulk-qr-layout"
                        >
                            <option value="3x4">3×4 (12/page · compact)</option>
                            <option value="2x2">2×2 (4/page · large)</option>
                            <option value="1x2">1×2 (2/page · XL bin)</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => downloadAuthed(
                                "/admin/products/qr-labels",
                                `qr-labels-${selectedIds.length}.pdf`,
                                { layout: qrLayout },
                                { method: "POST", body: selectedIds }
                            ).catch((e) => alert(e.message))}
                            className="btn-outline !py-1.5 !text-xs"
                            data-testid="bulk-qr-print"
                        >
                            <Printer className="w-3.5 h-3.5" /> Print {selectedIds.length} QR labels
                        </button>
                        <button
                            type="button"
                            onClick={() => setLabelEditorOpen(true)}
                            className="btn-primary !py-1.5 !text-xs"
                            data-testid="bulk-label-designer"
                            title="Design custom labels with editor"
                        >
                            <FileText className="w-3.5 h-3.5" /> Label Designer
                        </button>
                    </div>
                </div>
            )}

            <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-left">
                            <th className="px-2 py-2 w-9">
                                <input
                                    type="checkbox"
                                    checked={products.length > 0 && products.every((p) => selectedIds.includes(p.id))}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const merged = [...selectedIds];
                                            products.forEach((p) => { if (!merged.includes(p.id)) merged.push(p.id); });
                                            setSelectedIds(merged);
                                        } else {
                                            const visible = new Set(products.map((p) => p.id));
                                            setSelectedIds(selectedIds.filter((id) => !visible.has(id)));
                                        }
                                    }}
                                    className="accent-[hsl(var(--primary))]"
                                    data-testid="bulk-qr-select-all"
                                    title="Select all on this page"
                                />
                            </th>
                            <th className="px-3 py-2 label-kicker w-16">Img</th>
                            <th className="px-3 py-2 label-kicker">Name / Part #</th>
                            <th className="px-3 py-2 label-kicker">Category</th>
                            <th className="px-3 py-2 label-kicker">Stock</th>
                            <th className="px-3 py-2 label-kicker">Qty</th>
                            <th className="px-3 py-2 label-kicker">Price</th>
                            <th className="px-3 py-2 label-kicker text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.length === 0 && (
                            <tr><td colSpan="8" className="px-3 py-8 text-center font-mono text-slate-500">No products.</td></tr>
                        )}
                        {products.map((p) => {
                            const qty = p.stock_qty ?? 0;
                            const lowStock = qty > 0 && qty <= 5;
                            const outOfStock = qty === 0;
                            const isSel = selectedIds.includes(p.id);
                            return (
                            <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-amber-50" : ""}`} data-testid={`product-row-${p.id}`}>
                                <td className="px-2 py-2">
                                    <input
                                        type="checkbox"
                                        checked={isSel}
                                        onChange={() => setSelectedIds(isSel
                                            ? selectedIds.filter((x) => x !== p.id)
                                            : [...selectedIds, p.id])}
                                        className="accent-[hsl(var(--primary))]"
                                        data-testid={`bulk-qr-select-${p.id}`}
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <div className="w-12 h-12 bg-slate-50 border border-slate-200">
                                        {p.image_url ? (
                                            <img src={p.image_url} alt="" className="w-full h-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")} />
                                        ) : <Package className="w-6 h-6 text-slate-300 m-auto mt-3" />}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="font-semibold text-xs">{p.name}</div>
                                    <div className="font-mono text-[11px] text-slate-500">{p.part_number || "—"}</div>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-600">{p.category_name || "—"}</td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${p.stock === "in_stock" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                                        {p.stock === "in_stock" ? "in stock" : "on order"}
                                    </span>
                                </td>
                                <td className="px-3 py-2" data-testid={`product-qty-${p.id}`}>
                                    <span className={`font-mono font-bold text-xs px-1.5 py-0.5 ${outOfStock ? "bg-red-100 text-red-700" : lowStock ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                                        {qty}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-display font-bold">
                                    {p.price ? `€${p.price.toFixed(2)}` : <span className="text-slate-400 text-xs">—</span>}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openEdit(p)} className="p-1.5 text-slate-600 hover:text-[hsl(var(--primary))]" data-testid={`edit-product-${p.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => askRemove(p)} className="p-1.5 text-slate-600 hover:text-red-600" data-testid={`delete-product-${p.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        );})}
                    </tbody>
                </table>
            </div>

            {pages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-outline !py-1.5 !px-3">Prev</button>
                    <span className="font-mono text-slate-600">Page {page} / {pages} · {total} items</span>
                    <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="btn-outline !py-1.5 !px-3">Next</button>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="bg-white border border-slate-300 max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="product-edit-modal">
                        <div className="px-4 py-3 border-b border-slate-200 flex justify-between sticky top-0 bg-white z-10">
                            <div className="font-display font-bold uppercase">{editing.id ? "Edit" : "New"} product</div>
                            <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-4 grid md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Name *</label>
                                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-field" data-testid="product-name-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Category *</label>
                                <select value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} className="input-field" data-testid="product-category-input">
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">OEM Brand</label>
                                <select
                                    value={editing.brand_id || ""}
                                    onChange={(e) => setEditing({ ...editing, brand_id: e.target.value })}
                                    className="input-field"
                                    data-testid="product-brand-input"
                                >
                                    <option value="">— none —</option>
                                    {brands.filter((b) => b.kind === "oem").map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Aftermarket Brand</label>
                                <select
                                    value={editing.aftermarket_brand_id || ""}
                                    onChange={(e) => setEditing({ ...editing, aftermarket_brand_id: e.target.value })}
                                    className="input-field"
                                    data-testid="product-aftermarket-brand-input"
                                >
                                    <option value="">— none —</option>
                                    {brands.filter((b) => b.kind === "aftermarket").map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Part number</label>
                                <input value={editing.part_number} onChange={(e) => setEditing({ ...editing, part_number: e.target.value })} className="input-field" data-testid="product-partnumber-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Price (€)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editing.price}
                                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                                    className="input-field"
                                    placeholder="empty = no price"
                                    data-testid="product-price-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Stock</label>
                                <select value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="input-field" data-testid="product-stock-input">
                                    <option value="in_stock">In stock</option>
                                    <option value="on_order">On order</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Stock qty (admin)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={editing.stock_qty}
                                    onChange={(e) => setEditing({ ...editing, stock_qty: e.target.value })}
                                    className="input-field"
                                    placeholder="0"
                                    data-testid="product-stock-qty-input"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Main image</label>
                                <ImageUploader
                                    value={editing.image_url}
                                    onChange={(v) => setEditing({ ...editing, image_url: v })}
                                    testId="product-image-uploader"
                                    recommended="800×800px · WEBP/JPG · ~120KB · square, white/neutral background"
                                />
                            </div>

                            {/* Gallery */}
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Extra images (gallery)</label>
                                <div className="flex flex-wrap gap-2 items-start">
                                    <div className="flex gap-2 flex-1 min-w-[240px]">
                                        <input
                                            value={galleryInput}
                                            onChange={(e) => setGalleryInput(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGalleryUrl())}
                                            className="input-field flex-1"
                                            placeholder="Paste image URL and press Enter..."
                                            data-testid="product-gallery-input"
                                        />
                                        <button type="button" onClick={addGalleryUrl} className="btn-outline" data-testid="add-gallery-button">
                                            <Plus className="w-3.5 h-3.5" /> Add URL
                                        </button>
                                    </div>
                                    <label className="btn-outline cursor-pointer" data-testid="gallery-upload-label">
                                        <Upload className="w-3.5 h-3.5" /> Upload files
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files || []);
                                                for (const f of files) {
                                                    try {
                                                        const form = new FormData();
                                                        form.append("file", f);
                                                        const { data } = await api.post("/admin/upload", form, {
                                                            headers: { "Content-Type": "multipart/form-data" },
                                                        });
                                                        setEditing((prev) => ({ ...prev, gallery: [...prev.gallery, data.url] }));
                                                    } catch {}
                                                }
                                                e.target.value = "";
                                            }}
                                            data-testid="gallery-file-input"
                                        />
                                    </label>
                                </div>
                                {editing.gallery.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {editing.gallery.map((url, i) => (
                                            <div key={i} className="relative w-20 h-20 border border-slate-200 bg-slate-50" data-testid={`gallery-item-${i}`}>
                                                <img src={url} alt="" className="w-full h-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")} />
                                                <button
                                                    onClick={() => removeGalleryUrl(i)}
                                                    className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 flex items-center justify-center text-xs"
                                                    title="Remove"
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Note / Application</label>
                                <textarea
                                    rows={2}
                                    value={editing.note}
                                    onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                                    className="input-field"
                                    placeholder="e.g. Renault Avantime/ Vel Satis dCi, 2.2D, 110/150 kW, 2001-2006"
                                    data-testid="product-note-input"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Description</label>
                                <textarea
                                    rows={3}
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    className="input-field"
                                    data-testid="product-description-input"
                                />
                            </div>

                            {/* Usage table */}
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="label-kicker">Usage / Cross-reference</label>
                                    <button type="button" onClick={addUsageRow} className="btn-outline !py-1 !px-2 !text-[11px]" data-testid="add-usage-row">
                                        <Plus className="w-3 h-3" /> Add row
                                    </button>
                                </div>
                                {editing.usage.length === 0 ? (
                                    <div className="border border-dashed border-slate-300 p-3 text-xs font-mono text-slate-500 text-center">
                                        No rows. Click "Add row" to add cross-reference data (Turbo No., Car make/model, Engine, Year, OEM).
                                    </div>
                                ) : (
                                    <div className="border border-slate-200 overflow-x-auto">
                                        <table className="w-full text-xs font-mono">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                    {USAGE_COLUMNS.map((c) => (
                                                        <th key={c.key} className="px-2 py-1.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider">
                                                            {c.label}
                                                        </th>
                                                    ))}
                                                    <th className="px-2 py-1.5 w-8"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editing.usage.map((row, idx) => (
                                                    <tr key={idx} className="border-b border-slate-100" data-testid={`usage-row-${idx}`}>
                                                        {USAGE_COLUMNS.map((c) => (
                                                            <td key={c.key} className="p-1">
                                                                <input
                                                                    value={row[c.key] || ""}
                                                                    onChange={(e) => updateUsageCell(idx, c.key, e.target.value)}
                                                                    className="w-full px-1.5 py-1 border border-slate-200 text-xs focus:border-[hsl(var(--primary))] outline-none"
                                                                    data-testid={`usage-${idx}-${c.key}`}
                                                                />
                                                            </td>
                                                        ))}
                                                        <td className="p-1 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => removeUsageRow(idx)}
                                                                className="p-1 text-slate-400 hover:text-red-600"
                                                                data-testid={`remove-usage-${idx}`}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Specifications table */}
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="label-kicker">Specifications</label>
                                    <button type="button" onClick={addSpecRow} className="btn-outline !py-1 !px-2 !text-[11px]" data-testid="add-spec-row">
                                        <Plus className="w-3 h-3" /> Add spec
                                    </button>
                                </div>
                                {(!editing.specifications || editing.specifications.length === 0) ? (
                                    <div className="border border-dashed border-slate-300 p-3 text-xs font-mono text-slate-500 text-center">
                                        No specs. Click "Add spec" — e.g. Label "A", Value "34.60", Unit "mm".
                                    </div>
                                ) : (
                                    <div className="border border-slate-200">
                                        <table className="w-full text-xs font-mono">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider w-1/3">Label</th>
                                                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider w-1/3">Value</th>
                                                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider w-1/4">Unit</th>
                                                    <th className="px-2 py-1.5 w-8"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editing.specifications.map((row, idx) => (
                                                    <tr key={idx} className="border-b border-slate-100" data-testid={`spec-row-${idx}`}>
                                                        <td className="p-1">
                                                            <input
                                                                value={row.label || ""}
                                                                onChange={(e) => updateSpecCell(idx, "label", e.target.value)}
                                                                className="w-full px-1.5 py-1 border border-slate-200 text-xs focus:border-[hsl(var(--primary))] outline-none"
                                                                placeholder="A"
                                                                data-testid={`spec-${idx}-label`}
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <input
                                                                value={row.value || ""}
                                                                onChange={(e) => updateSpecCell(idx, "value", e.target.value)}
                                                                className="w-full px-1.5 py-1 border border-slate-200 text-xs focus:border-[hsl(var(--primary))] outline-none"
                                                                placeholder="34.60"
                                                                data-testid={`spec-${idx}-value`}
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <input
                                                                value={row.unit || ""}
                                                                onChange={(e) => updateSpecCell(idx, "unit", e.target.value)}
                                                                className="w-full px-1.5 py-1 border border-slate-200 text-xs focus:border-[hsl(var(--primary))] outline-none"
                                                                placeholder="mm"
                                                                data-testid={`spec-${idx}-unit`}
                                                            />
                                                        </td>
                                                        <td className="p-1 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => removeSpecRow(idx)}
                                                                className="p-1 text-slate-400 hover:text-red-600"
                                                                data-testid={`remove-spec-${idx}`}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {error && <div className="md:col-span-2 text-sm text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}
                        </div>
                        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 sticky bottom-0">
                            {editing.id && (
                                <button
                                    type="button"
                                    onClick={enrichFromSource}
                                    className="btn-outline mr-auto"
                                    title="Fetch gallery, note, and usage table from ee-turbo.eu"
                                    data-testid="enrich-from-source-button"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" /> Fetch from source
                                </button>
                            )}
                            <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
                            <button onClick={save} disabled={!editing.name || !editing.category_id} className="btn-primary" data-testid="save-product-button">
                                <Check className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                confirmText="Delete"
                destructive
                onConfirm={() => confirm?.onConfirm()}
                onCancel={() => setConfirm(null)}
            />

            {eeImport && (
                <EeTurboImportModal
                    state={eeImport}
                    onChange={setEeImport}
                    onClose={() => setEeImport(null)}
                    onImported={() => {
                        setEeImport(null);
                        load();
                    }}
                />
            )}

            <LabelEditor
                open={labelEditorOpen}
                onClose={() => setLabelEditorOpen(false)}
                initialProductIds={selectedIds}
            />
        </div>
    );
}
